import { useEffect, useCallback, useRef } from 'react';
import { globalWebSocket } from '../../../shared/ws/globalWebSocket';
import { WebSocketMessage } from '../types';
import { logger } from '../../../shared/utils/logger';

interface UseChatWebSocketProps {
  onMessage?: (message: WebSocketMessage) => void;
  onStatusChange?: (data: any) => void;
  onNewMessage?: (data: any) => void;
  onConnectionChange?: (status: 'connected' | 'disconnected' | 'error') => void;
}

export const useChatWebSocket = ({
  onMessage,
  onStatusChange,
  onNewMessage,
  onConnectionChange
}: UseChatWebSocketProps) => {
  // Храним актуальные колбэки в ref, чтобы не пересоздавать подписки
  const onMessageRef = useRef(onMessage);
  const onStatusChangeRef = useRef(onStatusChange);
  const onNewMessageRef = useRef(onNewMessage);
  const onConnectionChangeRef = useRef(onConnectionChange);

  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; }, [onStatusChange]);
  useEffect(() => { onNewMessageRef.current = onNewMessage; }, [onNewMessage]);
  useEffect(() => { onConnectionChangeRef.current = onConnectionChange; }, [onConnectionChange]);
  
  // Обработчики WebSocket событий (создаём подписки один раз)
  useEffect(() => {
    const handleNewMessage = (data: any) => {
      logger.debug('📨 Новое сообщение в чате:', data);
      onNewMessageRef.current?.(data);
    };

    const handleStatusChange = (data: any) => {
      logger.debug('🔄 Изменение статуса заказа:', data);
      onStatusChangeRef.current?.(data);
    };

    const handleMessage = (data: any) => {
      logger.debug('📨 WebSocket сообщение:', data);
      onMessageRef.current?.(data);
    };

    const handleConnectionEstablished = () => {
      logger.debug('✅ WebSocket соединение установлено');
      onConnectionChangeRef.current?.('connected');
    };

    const handleClose = () => {
      logger.debug('🔌 WebSocket соединение закрыто');
      onConnectionChangeRef.current?.('disconnected');
    };

    const handleError = () => {
      logger.error('❌ WebSocket ошибка');
      onConnectionChangeRef.current?.('error');
    };

    // Подписываемся на события
    // ❌ УБИРАЕМ new_message - обработка теперь только в realtimeSync.ts
    // globalWebSocket.on('new_message', handleNewMessage);
    globalWebSocket.on('status_change', handleStatusChange);
    globalWebSocket.on('message', handleMessage);
    globalWebSocket.on('connection_established', handleConnectionEstablished);
    globalWebSocket.on('close', handleClose);
    globalWebSocket.on('error', handleError);

    // Обрабатываем специальные события чата
    globalWebSocket.on('chat_message_sent', handleNewMessage);
    globalWebSocket.on('chat_message_received', handleNewMessage);
    globalWebSocket.on('order_status_changed', handleStatusChange);
    
    // Обрабатываем события от вебхуков Битрикса (new_message уже подписан выше)
    globalWebSocket.on('order_updated', handleStatusChange);
    globalWebSocket.on('offer_updated', handleStatusChange);

    return () => {
      // Отписываемся от событий
      // ❌ УБИРАЕМ new_message - обработка теперь только в realtimeSync.ts
      // globalWebSocket.off('new_message', handleNewMessage);
      globalWebSocket.off('status_change', handleStatusChange);
      globalWebSocket.off('message', handleMessage);
      globalWebSocket.off('connection_established', handleConnectionEstablished);
      globalWebSocket.off('close', handleClose);
      globalWebSocket.off('error', handleError);
      globalWebSocket.off('chat_message_sent', handleNewMessage);
      globalWebSocket.off('chat_message_received', handleNewMessage);
      globalWebSocket.off('order_status_changed', handleStatusChange);
      globalWebSocket.off('order_updated', handleStatusChange);
      globalWebSocket.off('offer_updated', handleStatusChange);
    };
  }, []);

  // Отправка сообщений через глобальный WebSocket
  const sendMessage = useCallback((message: string) => {
    globalWebSocket.send({
      type: 'chat_message',
      content: message,
      timestamp: new Date().toISOString()
    });
  }, []);

  const sendJsonMessage = useCallback((data: any) => {
    globalWebSocket.send(data);
  }, []);

  // Отправка активности устройства
  const sendDeviceActivity = useCallback(() => {
    globalWebSocket.send({
      type: 'device_activity',
      deviceId: `web_${Date.now()}`,
      isVisible: !document.hidden,
      isFocused: document.hasFocus(),
      lastActivity: Date.now(),
      userAgent: navigator.userAgent
    });
  }, []);

  // Отслеживание активности пользователя
  useEffect(() => {
    const handleVisibilityChange = () => sendDeviceActivity();
    const handleFocus = () => sendDeviceActivity();
    const handleActivity = () => sendDeviceActivity();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleFocus);
    
    // Отслеживаем активность пользователя
    const activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleFocus);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [sendDeviceActivity]);

  // Получение статуса соединения
  const getConnectionStatus = useCallback(() => {
    return globalWebSocket.getService().getConnectionStatus();
  }, []);

  return {
    sendMessage,
    sendJsonMessage,
    sendDeviceActivity,
    getConnectionStatus,
    isConnected: getConnectionStatus().isConnected
  };
};
